/**
 * Dashboard Service
 * Handles business logic for dashboard data
 */

import dashboardRepository from '../repositories/dashboard.repository'

class DashboardService {
  /**
   * Get date range based on filter selection
   */
  getDateRange(filter: string): { startDate: Date; endDate: Date } {
    const now = new Date()
    const endDate = new Date(now)
    let startDate = new Date(now)

    switch (filter) {
      case 'today':
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'yesterday':
        startDate.setDate(startDate.getDate() - 1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setDate(endDate.getDate() - 1)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'last7days':
        startDate.setDate(startDate.getDate() - 7)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'last30days':
        startDate.setDate(startDate.getDate() - 30)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'thisMonth':
        startDate.setDate(1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'lastMonth':
        startDate.setMonth(startDate.getMonth() - 1, 1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setMonth(endDate.getMonth() - 1)
        endDate.setDate(new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate())
        endDate.setHours(23, 59, 59, 999)
        break
      case 'thisQuarter':
        // Get the current quarter (0-3)
        const currentQuarter = Math.floor(now.getMonth() / 3)
        // Set start date to first day of current quarter
        startDate.setMonth(currentQuarter * 3, 1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case 'thisYear':
        // Set start date to January 1st of current year
        startDate.setMonth(0, 1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      default:
        // Default to this month
        startDate.setDate(1)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
    }

    return { startDate, endDate }
  }

  /**
   * Get dashboard data for specified time filter
   */
  async getDashboardData(timeFilter: string = 'thisMonth') {
    const { startDate, endDate } = this.getDateRange(timeFilter)

    try {
      // Fetch all data in parallel
      const [stats, mapData, rawData] = await Promise.all([
        dashboardRepository.getDashboardStats(startDate, endDate),
        dashboardRepository.getMapData(startDate, endDate),
        dashboardRepository.getGeographicIntelligence(startDate, endDate)
      ])

      return {
        stats,
        mapData,
        rawData,
        dateRange: { startDate, endDate }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      // Return default/empty data on error
      return {
        stats: {
          totalTraders: 0,
          activeTraders: 0,
          totalVolume: 0,
          liveAccountsPercent: 0,
          demoAccountsPercent: 0
        },
        mapData: new Map(),
        rawData: null,
        dateRange: { startDate, endDate }
      }
    }
  }

  /**
   * Format number for display
   */
  formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  /**
   * Get formatted period text based on filter
   */
  getFilterPeriodText(filter: string): string {
    const periodMap: { [key: string]: string } = {
      'today': 'Today',
      'yesterday': 'Yesterday',
      'last7days': 'Last 7 Days',
      'last30days': 'Last 30 Days',
      'thisMonth': 'This Month',
      'lastMonth': 'Last Month',
      'thisQuarter': 'This Quarter',
      'thisYear': 'This Year'
    }
    return periodMap[filter] || 'This Month'
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }
}

export default new DashboardService()